/**
 * 笔记明细列表页
 *
 * 品牌筛选 + 状态筛选 + 分页 DataGrid，展示每个品牌下的笔记明细。
 * 点击行打开详情弹窗，查看完整内容与配图。
 * 数据来源：GET /admin/notes
 */

import { useState, useEffect, useCallback } from 'react'
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Chip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Link,
  Stack,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { DataGrid, GridColDef } from '@mui/x-data-grid'
import { AdminLayout } from '@/components/AdminLayout'
import {
  getNotes,
  getBrands,
  type NoteListItem,
} from '@/api/services'
import type { Brand } from '@chaoyinli/shared'
import { formatDate } from '@/utils/format'

/** 模块类型中文映射 */
const MODULE_LABELS: Record<string, string> = {
  traffic: '种草引流',
  comment: '评论互动',
  other: '其他文案',
  brand: '品牌传播',
}

const STATUS_COLORS: Record<string, string> = {
  draft: '#FF9800',
  published: '#4CAF50',
}

export function NotesPage() {
  const [items, setItems] = useState<NoteListItem[]>([])
  const [brands, setBrands] = useState<Brand[]>([])
  const [brandFilter, setBrandFilter] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [detail, setDetail] = useState<NoteListItem | null>(null)

  useEffect(() => {
    getBrands()
      .then(setBrands)
      .catch(() => setBrands([]))
  }, [])

  const fetchNotes = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const data = await getNotes({
        brand_id: brandFilter || undefined,
        status: statusFilter || undefined,
        page: 1,
        page_size: 50,
      })
      setItems(data.items)
    } catch (e) {
      setError(e instanceof Error ? e.message : '加载笔记列表失败')
    } finally {
      setLoading(false)
    }
  }, [brandFilter, statusFilter])

  useEffect(() => {
    fetchNotes()
  }, [fetchNotes])

  const columns: GridColDef[] = [
    {
      field: 'images',
      headerName: '封面',
      width: 80,
      sortable: false,
      renderCell: (params) => {
        const imgs: string[] = params.value || []
        if (!imgs.length) return <Box sx={{ color: '#ccc' }}>—</Box>
        return (
          <Box
            component="img"
            src={imgs[0]}
            alt="封面"
            sx={{ width: 48, height: 48, objectFit: 'cover', borderRadius: '6px', mt: 1 }}
          />
        )
      },
    },
    {
      field: 'title',
      headerName: '标题',
      width: 220,
      valueGetter: (v) => (v ? String(v) : '(无标题)'),
    },
    { field: 'brand_name', headerName: '品牌', width: 140, valueGetter: (v) => v || '-' },
    { field: 'employee_name', headerName: '员工', width: 110, valueGetter: (v) => v || '-' },
    {
      field: 'module_type',
      headerName: '模块',
      width: 110,
      valueGetter: (v) => MODULE_LABELS[v as string] || v || '-',
    },
    {
      field: 'status',
      headerName: '状态',
      width: 100,
      renderCell: (params) => {
        const color = STATUS_COLORS[params.value] || '#999'
        return (
          <Chip
            label={params.value === 'published' ? '已发布' : '草稿'}
            size="small"
            sx={{ backgroundColor: `${color}15`, color, fontWeight: 600, fontSize: '12px' }}
          />
        )
      },
    },
    {
      field: 'created_at',
      headerName: '创建时间',
      width: 180,
      valueGetter: (v) => (v ? formatDate(v) : '-'),
    },
  ]

  return (
    <AdminLayout>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, color: '#333' }}>
          笔记明细
        </Typography>
        <Box sx={{ display: 'flex', gap: 1.5 }}>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>筛选品牌</InputLabel>
            <Select
              label="筛选品牌"
              value={brandFilter}
              onChange={(e) => setBrandFilter(e.target.value)}
            >
              <MenuItem value="">全部品牌</MenuItem>
              {brands.map((b) => (
                <MenuItem key={b.id} value={b.id}>
                  {b.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 130 }}>
            <InputLabel>状态</InputLabel>
            <Select
              label="状态"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <MenuItem value="">全部</MenuItem>
              <MenuItem value="draft">草稿</MenuItem>
              <MenuItem value="published">已发布</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box
        sx={{
          height: 'calc(100vh - 200px)',
          minHeight: 400,
          backgroundColor: '#fff',
          borderRadius: '12px',
        }}
      >
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress sx={{ color: '#FF8C00' }} />
          </Box>
        ) : (
          <DataGrid
            rows={items}
            columns={columns}
            getRowId={(row) => row.id}
            pageSizeOptions={[10, 25, 50]}
            initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
            onRowClick={(p) => setDetail(p.row as NoteListItem)}
            sx={{
              border: 'none',
              '& .MuiDataGrid-cell:focus': { outline: 'none' },
              '& .MuiDataGrid-columnHeader': { backgroundColor: '#fafafa' },
              '& .MuiDataGrid-row': { cursor: 'pointer' },
            }}
          />
        )}
      </Box>

      {/* 笔记详情弹窗 */}
      <Dialog open={!!detail} onClose={() => setDetail(null)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span>笔记详情</span>
          <IconButton onClick={() => setDetail(null)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {detail && (
            <Stack spacing={2}>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Chip label={detail.brand_name} size="small" color="warning" variant="outlined" />
                <Chip label={detail.employee_name} size="small" variant="outlined" />
                <Chip
                  label={MODULE_LABELS[detail.module_type] || detail.module_type}
                  size="small"
                  variant="outlined"
                />
                <Chip
                  label={detail.status === 'published' ? '已发布' : '草稿'}
                  size="small"
                  sx={{
                    backgroundColor: `${STATUS_COLORS[detail.status] || '#999'}15`,
                    color: STATUS_COLORS[detail.status] || '#999',
                  }}
                />
              </Box>

              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                {detail.title || '(无标题)'}
              </Typography>

              {detail.tags?.length > 0 && (
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                  {detail.tags.map((t, i) => (
                    <Chip key={i} label={t} size="small" />
                  ))}
                </Box>
              )}

              <Typography
                variant="body2"
                sx={{ whiteSpace: 'pre-wrap', color: '#333', lineHeight: 1.8 }}
              >
                {detail.content || '(无正文)'}
              </Typography>

              {detail.images?.length > 0 && (
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {detail.images.map((src, i) => (
                    <Box
                      key={i}
                      component="img"
                      src={src}
                      alt={`配图${i + 1}`}
                      sx={{ width: 120, height: 120, objectFit: 'cover', borderRadius: '8px' }}
                    />
                  ))}
                </Box>
              )}

              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="caption" sx={{ color: '#999' }}>
                  创建：{detail.created_at ? formatDate(detail.created_at) : '-'}
                  {detail.published_at
                    ? ` ｜ 发布：${formatDate(detail.published_at)}`
                    : ''}
                </Typography>
                {detail.xhs_note_url && (
                  <Link href={detail.xhs_note_url} target="_blank" rel="noopener" underline="hover">
                    小红书链接
                  </Link>
                )}
              </Box>
            </Stack>
          )}
        </DialogContent>
      </Dialog>
    </AdminLayout>
  )
}

export default NotesPage
